import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import React from 'react';
import SignUpPage from './pages/SignUp';
import LoginPage from './pages/Login';
import styled from 'styled-components';
import { Router } from '@reach/router';
import FourOhFourPage from './pages/404';
import { UserProvider, UserContextConsumer } from './DataProviders/User';
import Conditional from './components/Conditional';
import Nav from './components/Nav';
import Directory from './pages/Directory';
import AppFooter from './components/AppFooter';
import Enrichmentlist from './pages/EnrichmentList';
import User from './Entities/User';
import ManageAnimals from './pages/ManageAnimals';
import Comments from './pages/Comments';
import EditAnimal from './pages/EditAnimal';
import { AnimalOptionsProvider } from './DataProviders/AnimalOptions';
import CreateAnimal from './pages/CreateAnimal';
import ManageLocations from './pages/ManageLocations';
import Admin from './pages/Admin';
import AnimalAdminOptions from './pages/AnimalAdminOptions';
import SpecialNeedsAdminOptions from './pages/SpecialNeedsAdminOptions';
import LiveData from './pages/LiveData';
import MedicalData from './pages/MedicalData';
import EditEnrichments from './pages/EditEnrichments';
import Report from './pages/Report';
import EvaluationsRoomList from './pages/EvaluationsRoomList';
import EvaluationsSingleRoom from './pages/EvaluationsSingleRoom';
import EvaluationSingleAnimalStatistics from './pages/EvaluationSingleAnimalStatistics';
import { Box } from 'bloomer/lib/elements/Box';
import { Container } from 'bloomer/lib/layout/Container';
import LoadingSpinner from './elements/LoadingSpinner';
import * as Sentry from '@sentry/browser';
import { Title } from 'bloomer/lib/elements/Title';
import CustomContentAdminOptions from './pages/CustomContentAdminOptions';
import Analytics from './Analytics';

const Main = styled.main`
  margin: 35px 0;
  min-height: 60vh;
`;

const LoadingMessage = styled.p`
  margin: 20px;
`;

const AuthRouter = () => (
  <Analytics id="UA-97384740-2">
    <Router>
      <Directory path="/" />
      <Enrichmentlist path="/list/:animalType" />
      {/* <!-- Manage Animals --> */}
      <ManageAnimals path="/manageAnimals/animalList" />
      <Comments path="/manageAnimals/comments/:aNumber" />
      <EditAnimal path="/manageAnimals/edit/:aNumber" />
      <CreateAnimal path="/manageAnimals/addAnimal" />
      <ManageLocations path="/manageAnimals/locations" />
      {/* <!-- Admin --> */}
      <Admin path="/admin/general" />
      <AnimalAdminOptions path="/admin/:animalType" />
      <SpecialNeedsAdminOptions path="/admin/specialNeeds/:animalType" />
      <CustomContentAdminOptions path="/admin/customContent" />
      {/* <!-- Statistics --> */}
      <LiveData path="/stats/live/:animalType" />
      <MedicalData path="/stats/medical" />
      <EditEnrichments path="/stats/editEnrichments" />
      <Report path="/stats/report" />
      <FourOhFourPage path="*" />
      {/* Evaluations */}
      <EvaluationsRoomList path="/evaluations" />
      <EvaluationsSingleRoom path="/evaluations/:room" />
      <EvaluationSingleAnimalStatistics path="/statistics/evaluations/:aNumber" />
    </Router>
  </Analytics>
);

const PublicRouter = () => (
  <Router>
    <SignUpPage path="/signup" />
    <LoginPage path="*" />
  </Router>
);

const getRouter = (user: User) => {
  if (user.loggedIn && user.admin === 'disabled') {
    return (
      <Container>
        <Box>
          <Title>NO ACCESS:</Title>
          <p>
            Your account is currently set to "disabled". Please ask an administrator to grant you access if you feel
            this is a mistake.
          </p>
        </Box>
      </Container>
    );
  }

  if (user.loggedIn) {
    return <AuthRouter />;
  }

  return <PublicRouter />;
};

const App = () => {
  Sentry.configureScope(scope => {
    scope.setExtra('page_url', window.location.href);
  });

  return (
    <UserProvider>
      <AnimalOptionsProvider>
        <UserContextConsumer>
          {({ user, loading }) => {
            if (loading) {
              return (
                <Main>
                  <Container>
                    <Box>
                      <LoadingSpinner />
                      <Conditional check={Boolean(loading)}>
                        <LoadingMessage className="has-text-centered is-size-3">
                          Loading user profiles...
                        </LoadingMessage>
                      </Conditional>
                    </Box>
                  </Container>
                </Main>
              );
            }

            const AppRouter = getRouter(user);
            return (
              <>
                <Conditional check={user.loggedIn}>
                  <Nav user={user} />
                </Conditional>
                <Main>{AppRouter}</Main>
                <Conditional check={user.loggedIn}>
                  <AppFooter />
                </Conditional>
              </>
            );
          }}
        </UserContextConsumer>
      </AnimalOptionsProvider>
    </UserProvider>
  );
};

export default App;
